import { html, TemplateResult } from 'lit';
import { msg } from '@lit/localize';
import type { ImageRenderData } from '../utils/gallery.utils.js';
import { galleryImageTemplate } from './gallery-image.template.js';

function getNormalizedCaption(alt: string | undefined): string {
    const caption = alt?.trim() ?? '';
    return caption && caption.toLowerCase() !== 'none' ? caption : '';
}

/**
 * Renders the gallery fullscreen template.
 * @param images - Flat list of overlay images
 * @param activeIndex - Current visible slide index
 * @returns Fullscreen template
 */
export function galleryFullscreenTemplate(images: ImageRenderData[], activeIndex: number): TemplateResult {
    const current = String(activeIndex + 1).padStart(2, '0');
    const total = String(images.length).padStart(2, '0');
    const activeCaption = getNormalizedCaption(images[activeIndex]?.alt);
    const hasActiveCaption = Boolean(activeCaption);
    const activeCaptionText = hasActiveCaption ? activeCaption : '\u00a0';

    return html`
        <div class="der-tm-roundtrip-gallery-overlay__fullscreen" tabindex="-1" aria-hidden="false">
            <div class="embla">
                <div class="embla__viewport">
                    <div class="embla__container">
                        ${images.map((image) => {
                            const slideCaption = getNormalizedCaption(image.alt);
                            const hasSlideCaption = Boolean(slideCaption);
                            const slideCaptionText = hasSlideCaption ? slideCaption : '\u00a0';

                            return html`<div class="embla__slide">
                                <div class="embla__slide-image-area">
                                    ${galleryImageTemplate(image, 'eager', undefined, -1)}
                                    <p
                                        class="der-tm-roundtrip-gallery-overlay__slide-caption ${
                                            hasSlideCaption
                                                ? ''
                                                : 'der-tm-roundtrip-gallery-overlay__slide-caption--placeholder'
                                        }"
                                        aria-hidden=${hasSlideCaption ? 'false' : 'true'}
                                    >
                                        ${slideCaptionText}
                                    </p>
                                </div>
                            </div> `;
                        })}
                    </div>
                </div>

                <button
                    class="embla__prev control-button control-prev"
                    data-embla-prev
                    type="button"
                    aria-label=${msg('Vorheriges Bild', {
                        desc: 'Aria label for the previous image button in fullscreen gallery slider',
                    })}
                >
                    <der-ui-controls
                        direction="left"
                        ariaLabel="${msg('Vorheriges Bild', {
                            desc: 'Aria label for the previous image button in fullscreen gallery slider',
                        })}"
                    ></der-ui-controls>
                </button>
                <button
                    class="embla__next control-button control-next"
                    data-embla-next
                    type="button"
                    aria-label=${msg('Nächstes Bild', {
                        desc: 'Aria label for the next image button in fullscreen gallery slider',
                    })}
                >
                    <der-ui-controls
                        direction="right"
                        ariaLabel="${msg('Nächstes Bild', {
                            desc: 'Aria label for the next image button in fullscreen gallery slider',
                        })}"
                    ></der-ui-controls>
                </button>
            </div>
            <div class="der-tm-roundtrip-gallery-overlay__footer">
                <div class="der-tm-roundtrip-gallery-overlay__caption-wrapper">
                    <p
                        class="der-tm-roundtrip-gallery-overlay__caption ${
                            hasActiveCaption ? '' : 'der-tm-roundtrip-gallery-overlay__caption--placeholder'
                        }"
                        aria-hidden=${hasActiveCaption ? 'false' : 'true'}
                    >
                        ${activeCaptionText}
                    </p>
                </div>
                <div class="der-tm-roundtrip-gallery-overlay__pagination-wrapper">
                    <button
                        class="der-tm-roundtrip-gallery-overlay__mobile-control"
                        data-embla-prev
                        type="button"
                        aria-label=${msg('Vorheriges Bild', {
                            desc: 'Aria label for the previous image button in fullscreen gallery slider',
                        })}
                    >
                        <der-ui-controls
                            direction="left"
                            ariaLabel="${msg('Vorheriges Bild', {
                                desc: 'Aria label for the previous image button in fullscreen gallery slider',
                            })}"
                        ></der-ui-controls>
                    </button>
                    <p class="der-tm-roundtrip-gallery-overlay__pagination" aria-live="polite">
                        <span class="der-tm-roundtrip-gallery-overlay__pagination-current">${current}</span>
                        <span class="der-tm-roundtrip-gallery-overlay__pagination-separator">/</span>
                        <span class="der-tm-roundtrip-gallery-overlay__pagination-total">${total}</span>
                    </p>
                    <button
                        class="der-tm-roundtrip-gallery-overlay__mobile-control"
                        data-embla-next
                        type="button"
                        aria-label=${msg('Nächstes Bild', {
                            desc: 'Aria label for the next image button in fullscreen gallery slider',
                        })}
                    >
                        <der-ui-controls
                            direction="right"
                            ariaLabel="${msg('Nächstes Bild', {
                                desc: 'Aria label for the next image button in fullscreen gallery slider',
                            })}"
                        ></der-ui-controls>
                    </button>
                </div>
            </div>
        </div>
    `;
}
